import { getWechatJssdkSignature } from "@/lib/api/generated/endpoints/wechat/wechat";
import type { WechatJssdkSignatureResponse } from "@/lib/api/generated/model";

const wechatJssdkConfigPromiseCache = new Map<string, Promise<void>>();
const wechatJssdkSignaturePromiseCache = new Map<string, Promise<WechatJSSDKConfigResponse>>();

export function hasWechatJssdk(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  return Boolean(window.wx && typeof window.wx.config === "function");
}

export type WechatJSSDKConfigResponse = WechatJssdkSignatureResponse;

type WechatShareData = {
  title: string;
  desc: string;
  link: string;
  imgUrl: string;
};

export type WechatShareDataPayload = WechatShareData & {
  type?: "link" | "video" | "music" | "music|video";
};

function waitForWechatJssdk(signal?: AbortSignal): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }

  if (window.wx) {
    return Promise.resolve();
  }

  const maxWaitMs = 5000;
  const checkIntervalMs = 100;

  return new Promise<void>((resolve, reject) => {
    const cleanup = () => {
      clearInterval(timer);
      clearTimeout(timeout);
      signal?.removeEventListener("abort", onAbort);
    };

    const onAbort = () => {
      cleanup();
      reject(signal?.reason ?? new DOMException("Request was aborted", "AbortError"));
    };

    const timer = setInterval(() => {
      if (window.wx) {
        cleanup();
        resolve();
      }
    }, checkIntervalMs);

    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error("微信 JSSDK 未能在 5s 内完成初始化"));
    }, maxWaitMs + checkIntervalMs);

    if (signal?.aborted) {
      onAbort();
      return;
    }

    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

function createAbortError(signal?: AbortSignal): Error {
  if (signal?.reason instanceof Error) {
    return signal.reason;
  }

  return new DOMException("Request was aborted", "AbortError");
}

export function prefetchWechatJssdkSignature(
  currentPageUrl: string,
  signal?: AbortSignal,
): Promise<WechatJSSDKConfigResponse> {
  const cached = wechatJssdkSignaturePromiseCache.get(currentPageUrl);
  if (cached) {
    return cached;
  }

  const request = (async () => {
    const signature = await getWechatJssdkSignature(
      { url: currentPageUrl },
      {
        cache: "no-store",
        signal,
      },
    );
    if (!signature.ok) {
      throw new Error("微信签名接口返回异常");
    }

    return signature;
  })();

  wechatJssdkSignaturePromiseCache.set(currentPageUrl, request);

  return request.catch((error) => {
    wechatJssdkSignaturePromiseCache.delete(currentPageUrl);
    throw error;
  });
}

export function ensureWechatJssdkConfigured(
  currentPageUrl: string,
  options?: { signal?: AbortSignal },
): Promise<void> {
  const cached = wechatJssdkConfigPromiseCache.get(currentPageUrl);
  if (cached) {
    return cached;
  }

  const setup = (async () => {
    if (typeof window === "undefined") {
      return;
    }

    await waitForWechatJssdk(options?.signal);

    const signature = await prefetchWechatJssdkSignature(currentPageUrl, options?.signal);

    if (!signature.ok) {
      throw new Error("微信签名接口返回异常");
    }

    if (options?.signal?.aborted) {
      throw createAbortError(options.signal);
    }

    if (!window.wx) {
      throw new Error("微信 JSSDK 未成功注入");
    }

    await new Promise<void>((resolve, reject) => {
      window.wx!.error(reject);
      window.wx!.ready(resolve);
      window.wx!.config({
        debug: signature.debug,
        appId: signature.appId,
        timestamp: signature.timestamp,
        nonceStr: signature.nonceStr,
        signature: signature.signature,
        jsApiList: signature.jsApiList,
        openTagList: signature.openTagList,
      });
    });
  })();

  wechatJssdkConfigPromiseCache.set(currentPageUrl, setup);

  return setup.catch((error) => {
    wechatJssdkConfigPromiseCache.delete(currentPageUrl);
    throw error;
  });
}

export function applyWechatShareData(payload: WechatShareDataPayload) {
  if (!window.wx) {
    return;
  }

  if (window.wx.updateAppMessageShareData) {
    window.wx.updateAppMessageShareData({
      ...payload,
      type: "link",
      success: () => {
        // no-op
      },
    });
  } else if (window.wx.onMenuShareAppMessage) {
    window.wx.onMenuShareAppMessage(payload);
  }

  if (window.wx.updateTimelineShareData) {
    window.wx.updateTimelineShareData({
      title: payload.title,
      link: payload.link,
      imgUrl: payload.imgUrl,
      success: () => {
        // no-op
      },
    });
  } else if (window.wx.onMenuShareTimeline) {
    window.wx.onMenuShareTimeline({
      title: payload.title,
      link: payload.link,
      imgUrl: payload.imgUrl,
      success: () => {
        // no-op
      },
    });
  }
}
